import { amber, blue, green, grey, orange, red } from "@mui/material/colors";

export function normalizeDoorStatus(status) {
    if (status === 'inativado') return 'desativado';

    return status ?? 'livre';
}

export function getEffectiveDoorStatus(porta) {
    if (!porta) return 'livre';

    return porta.status === 'grouped'
        ? normalizeDoorStatus(porta.doorStatus)
        : normalizeDoorStatus(porta.status);
}

export function isInactiveDoor(porta) {
    return getEffectiveDoorStatus(porta) === 'desativado';
}

// Porta "ocupada" = tem produto vinculado. Usado para impedir agrupamento.
export function isPortaOcupada(porta) {
    if (!porta) return false;

    const status = getEffectiveDoorStatus(porta);

    return status === 'ocupado' ||
        (porta.id_produto !== null && porta.id_produto !== undefined);
}

export function rectIndices(firstIdx, secondIdx, cols, totalPortas) {
    const safeCols = Math.max(1, parseInt(cols) || 1);

    const firstRow = Math.floor(firstIdx / safeCols);
    const firstCol = firstIdx % safeCols;
    const secondRow = Math.floor(secondIdx / safeCols);
    const secondCol = secondIdx % safeCols;

    const minRow = Math.min(firstRow, secondRow);
    const maxRow = Math.max(firstRow, secondRow);
    const minCol = Math.min(firstCol, secondCol);
    const maxCol = Math.max(firstCol, secondCol);

    const indices = [];

    for (let row = minRow; row <= maxRow; row++) {
        for (let col = minCol; col <= maxCol; col++) {
            const index = row * safeCols + col;

            if (index >= 0 && index < totalPortas) {
                indices.push(index);
            }
        }
    }

    return indices;
}

export function rectIsValid(firstIdx, secondIdx, portas, cols, totalPortas) {
    if (
        firstIdx === null ||
        secondIdx === null ||
        firstIdx === undefined ||
        secondIdx === undefined
    ) {
        return false;
    }

    const safeCols = Math.max(1, parseInt(cols) || 1);

    const firstRow = Math.floor(firstIdx / safeCols);
    const firstCol = firstIdx % safeCols;
    const secondRow = Math.floor(secondIdx / safeCols);
    const secondCol = secondIdx % safeCols;

    const expectedRows = Math.abs(secondRow - firstRow) + 1;
    const expectedCols = Math.abs(secondCol - firstCol) + 1;

    const indices = rectIndices(firstIdx, secondIdx, safeCols, totalPortas);

    if (indices.length !== expectedRows * expectedCols) {
        return false;
    }

    return indices.every(index => {
        const porta = portas[index];

        if (!porta) {
            return false;
        }

        if (porta.status !== 'livre') {
            return false;
        }

        if (porta.groupId !== null && porta.groupId !== undefined) {
            return false;
        }

        return !isInactiveDoor(porta);
    });
}

// Retorna o conjunto de índices de portas que, combinadas com firstIdx,
// formam um retângulo VÁLIDO para agrupamento. Usado para habilitar só essas
// portas (as demais ficam disabled) durante a seleção.
export function getGroupableWithFirst(firstIdx, portas, cols, totalPortas) {
    const result = new Set();

    if (firstIdx === null || firstIdx === undefined) {
        return result;
    }

    for (let idx = 0; idx < portas.length; idx++) {
        if (idx === firstIdx) continue;

        if (rectIsValid(firstIdx, idx, portas, cols, totalPortas)) {
            result.add(idx);
        }
    }

    return result;
}

export function getNextGroupId(portas) {
    const ids = portas
        .map(porta => Number(porta.groupId))
        .filter(groupId => Number.isFinite(groupId) && groupId > 0);

    return ids.length > 0 ? Math.max(...ids) + 1 : 1;
}

export function recalcLogical(portas = []) {
    const updated = portas.map((porta, index) => ({
        ...porta,
        physIdx: index,
        status: porta.status === 'grouped' ? 'grouped' : normalizeDoorStatus(porta.status),
        doorStatus: porta.status === 'grouped' ? normalizeDoorStatus(porta.doorStatus) : null,
    }));

    const processedGroups = new Set();

    let nextLogicalNumber = 1;

    for (let index = 0; index < updated.length; index++) {
        const porta = updated[index];

        if (porta.status === 'grouped') {
            const groupKey = String(porta.groupId);

            if (processedGroups.has(groupKey)) {
                continue;
            }

            processedGroups.add(groupKey);

            const memberIndexes = updated
                .map((member, memberIndex) => ({ member, memberIndex }))
                .filter(({ member }) => member.status === 'grouped' && String(member.groupId) === groupKey)
                .map(({ memberIndex }) => memberIndex)
                .sort((a, b) => a - b);

            if (memberIndexes.length === 0) {
                continue;
            }

            const primaryIndex = memberIndexes[0];

            const existingLogicalNumber = memberIndexes
                .map(memberIndex => updated[memberIndex].numero_logico)
                .find(numero => numero !== null && numero !== undefined);

            const inactive = isInactiveDoor(updated[primaryIndex]);

            const logicalNumber = inactive
                ? (existingLogicalNumber ?? primaryIndex + 1)
                : nextLogicalNumber++;

            memberIndexes.forEach(memberIndex => {
                updated[memberIndex] = {
                    ...updated[memberIndex],
                    physIdx: memberIndex,
                    status: 'grouped',
                    groupId: porta.groupId,
                    isPrimary: memberIndex === primaryIndex,
                    numero_logico: logicalNumber,
                };
            });

            continue;
        }

        const inactive = isInactiveDoor(porta);

        const logicalNumber = inactive
            ? (porta.numero_logico ?? index + 1)
            : nextLogicalNumber++;

        updated[index] = {
            ...porta,
            physIdx: index,
            status: normalizeDoorStatus(porta.status),
            doorStatus: null,
            groupId: null,
            isPrimary: false,
            numero_logico: logicalNumber,
        };
    }

    return updated;
}

export function parsePortasFisicas(porta, totalFisicas, cols) {
    let rawFisicas = porta?.portasFisicas ?? porta?.portas_fisicas ?? [];

    try {
        if (typeof rawFisicas === 'string') {
            rawFisicas = JSON.parse(rawFisicas);
        }
    } catch {
        rawFisicas = [];
    }

    if (!Array.isArray(rawFisicas) || rawFisicas.length === 0) {
        const linha = Number(porta?.linhaInicial ?? porta?.linha_inicial ?? 0);
        const coluna = Number(porta?.colunaInicial ?? porta?.coluna_inicial ?? 0);

        rawFisicas = [linha * cols + coluna + 1];
    }

    return [
        ...new Set(
            rawFisicas
                .map(numero => Number(numero))
                .filter(numero => Number.isInteger(numero) && numero >= 1 && numero <= totalFisicas)
        )
    ].sort((a, b) => a - b);
}

export function normalizePortasFromBackend({
                                               portasApi = [],
                                               totalFisicas,
                                               cols,
                                               idArmario = null,
                                           }) {
    const safeTotal = Math.max(0, Number(totalFisicas) || 0);
    const safeCols = Math.max(1, Number(cols) || 1);

    const fisicas = Array.from({ length: safeTotal }, (_, index) => ({
        id: null,
        id_armario: idArmario,
        id_produto: null,
        id_produto_pai: null,
        mustSelectChild: false,
        numero_logico: index + 1,
        physIdx: index,
        status: 'livre',
        doorStatus: null,
        groupId: null,
        isPrimary: false,
        multiplicador: 1,
        portas_fisicas: [index + 1],
        qtd_produto: 0,
        is_dirty: false,
    }));

    let fallbackGroupCounter = 0;

    portasApi.forEach(porta => {
        const portasFisicas = parsePortasFisicas(porta, safeTotal, safeCols);

        if (portasFisicas.length === 0) {
            return;
        }

        const fisicasIdxs = portasFisicas.map(numero => numero - 1);
        const isAgrupado = fisicasIdxs.length > 1;

        const groupId = isAgrupado
            ? (
                porta?.id !== null && porta?.id !== undefined
                    ? `backend-${porta.id}`
                    : `backend-temp-${++fallbackGroupCounter}`
            )
            : null;

        const configuracoes = Array.isArray(porta?.configuracoes) ? porta.configuracoes : [];

        const configProduto = configuracoes.find(config =>
            config?.produto?.id !== null && config?.produto?.id !== undefined
        ) ?? configuracoes[0] ?? null;

        const idProduto = configProduto?.produto?.id ?? null;
        const idProdutoPai = configProduto?.produto?.pai ?? null;

        const quantidade = Number(configProduto?.quantidade ?? 0);
        const statusApi = normalizeDoorStatus(porta?.status);

        fisicasIdxs.forEach((physIdx, indexNoGrupo) => {
            fisicas[physIdx] = {
                ...fisicas[physIdx],
                id: porta?.id ?? null,
                id_armario: idArmario,
                numero_logico: porta?.numeroLogico ?? porta?.numero_logico ?? null,
                physIdx,
                status: isAgrupado ? 'grouped' : statusApi,
                doorStatus: isAgrupado ? statusApi : null,
                groupId,
                isPrimary: indexNoGrupo === 0,
                portas_fisicas: portasFisicas,
                id_produto: idProduto,
                id_produto_pai: idProdutoPai,
                qtd_produto: Number.isFinite(quantidade) ? quantidade : 0,
                mustSelectChild: false,
                is_dirty: false,
            };
        });
    });

    return recalcLogical(fisicas);
}

export function buildLockerCells(portas = [], cols = 1) {
    const safeCols = Math.max(1, Number(cols) || 1);

    const cells = [];
    const seenGroups = new Set();

    portas.forEach((porta, physIdx) => {
        const row = Math.floor(physIdx / safeCols);
        const col = physIdx % safeCols;

        if (porta.status !== 'grouped') {
            cells.push({
                porta,
                configDoor: porta,
                members: [porta],
                physIdx,
                gridRow: row + 1,
                gridCol: col + 1,
                rowSpan: 1,
                colSpan: 1,
                grouped: false,
            });

            return;
        }

        const groupKey = String(porta.groupId);

        if (seenGroups.has(groupKey)) {
            return;
        }

        const membersWithIndex = portas
            .map((member, index) => ({ member, index }))
            .filter(({ member }) =>
                member.status === 'grouped' && String(member.groupId) === groupKey
            )
            .sort((a, b) => a.index - b.index);

        if (membersWithIndex.length === 0) {
            return;
        }

        seenGroups.add(groupKey);

        const primaryEntry = membersWithIndex.find(({ member }) => member.isPrimary) ?? membersWithIndex[0];

        const primaryDoor = primaryEntry.member;
        const primaryIndex = primaryEntry.index;

        const indexes = membersWithIndex.map(({ index }) => index);

        const rows = indexes.map(index => Math.floor(index / safeCols));
        const columns = indexes.map(index => index % safeCols);

        const minRow = Math.min(...rows);
        const maxRow = Math.max(...rows);
        const minCol = Math.min(...columns);
        const maxCol = Math.max(...columns);

        cells.push({
            porta: primaryDoor,
            configDoor: primaryDoor,
            members: membersWithIndex.map(({ member }) => member),
            memberIndexes: indexes,
            physIdx: primaryIndex,
            gridRow: minRow + 1,
            gridCol: minCol + 1,
            rowSpan: maxRow - minRow + 1,
            colSpan: maxCol - minCol + 1,
            grouped: true,
        });
    });

    return cells;
}

export function getDoorColors(porta) {
    const status = getEffectiveDoorStatus(porta);

    if (status === 'desativado') {
        return {
            bg: red[700],
            hoverBg: red[800],
            border: red[900],
            color: 'white'
        };
    }

    if (status === 'aguardando' || status === 'bloqueado') {
        return {
            bg: red[700],
            hoverBg: red[900],
            border: red[900],
            color: 'white'
        };
    }

    if (porta?.id_produto) {
        return {
            bg: green[900],
            hoverBg: green[800],
            border: green[900],
            color: 'white'
        };
    }

    return {
        bg: green[200],
        hoverBg: green[600],
        border: green[400],
        color: green[900]
    };
}

// Cores por status para o SecondStep (livre/ocupada/inativada), no mesmo
// formato usado nas células e na borda tracejada da agrupada.
export function statusColorsForDoor(porta) {
    const status = getEffectiveDoorStatus(porta);

    if (status === 'desativado') {
        return { bg: red[700], hoverBg: red[800], border: red[900], color: 'white' };
    }

    // Ocupada = tem produto vinculado.
    if (status === 'ocupado' || porta?.id_produto) {
        return { bg: green[900], hoverBg: green[800], border: green[900], color: 'white' };
    }

    // Livre
    return { bg: green[200], hoverBg: green[300], border: green[400], color: green[900] };
}

// ============================================================================
// CORES / LEGENDA — paleta.js unificada (igual ao fluxo da Máquina)
// ----------------------------------------------------------------------------
// ocupada          -> verde escuro   (green[900])
// livre            -> verde claro    (green[200])
// inativada        -> vermelho       (red[700])
// selecionada      -> amarelo        (amber[300])  [aplicado no componente]
// disponível agrup.-> azul claro     (blue[100])   [aplicado no componente]
// agrupada         -> mantém a cor do STATUS + borda tracejada destacada
// ============================================================================

// Borda destacada usada para sinalizar um grupo (mantendo a cor do status).
export const GROUP_BORDER = `3px dashed ${grey[800]}`;

// Legenda completa (SecondStep — tem agrupamento).
export const LOCKER_LEGEND_GROUPING = [
    { label: 'Porta Selecionada', bg: amber[300], border: amber[600] },
    { label: 'Disponível p/ agrupar', bg: blue[100], border: blue[400] },
    { label: 'Porta Livre', bg: green[200], border: green[400] },
    { label: 'Porta Ocupada', bg: green[900], border: green[900] },
    { label: 'Porta Agrupada', bg: green[200], border: grey[800], dashed: true },
    { label: 'Porta Inativada', bg: red[700], border: red[900] },
];

// Legenda de status (ThirdStep / Restocking — sem seleção de agrupamento).
export const LOCKER_LEGEND = [
    { label: 'Porta Selecionada', bg: amber[300], border: amber[600] },
    { label: 'Porta Livre', bg: green[200], border: green[400] },
    { label: 'Porta Ocupada', bg: green[900], border: green[900] },
    { label: 'Porta Agrupada', bg: green[200], border: grey[800], dashed: true },
    { label: 'Porta Inativada', bg: red[700], border: red[900] },
];

export function buildPortasPayload(portas = [], qtdColunas = 1) {
    const cols = Math.max(1, parseInt(qtdColunas) || 1);

    const seenGroups = new Set();

    const result = [];

    portas.forEach((porta, physIdx) => {
        if (porta.status === 'grouped') {
            const groupKey = String(porta.groupId);

            if (seenGroups.has(groupKey)) {
                return;
            }

            seenGroups.add(groupKey);

            const members = portas
                .map((member, index) => ({ ...member, _physIdx: index }))
                .filter(member =>
                    member.status === 'grouped' && String(member.groupId) === groupKey
                )
                .sort((a, b) => (a.physIdx ?? a._physIdx) - (b.physIdx ?? b._physIdx));

            if (members.length === 0) {
                return;
            }

            const primary = members.find(member => member.isPrimary) ?? members[0];

            const idxs = members.map(member => member.physIdx ?? member._physIdx);

            const rows = idxs.map(index => Math.floor(index / cols));
            const columns = idxs.map(index => index % cols);

            const linhaInicial = Math.min(...rows);
            const colunaInicial = Math.min(...columns);

            const numeroLinhas = Math.max(...rows) - linhaInicial + 1;
            const numeroColunas = Math.max(...columns) - colunaInicial + 1;

            const configuracoes = primary.id_produto
                ? [
                    {
                        id_produto: primary.id_produto,
                        quantidade: primary.qtd_produto ?? 0,
                    }
                ]
                : [];

            result.push({
                numeroLogico: primary.numero_logico,
                portasFisicas: idxs.map(index => index + 1),
                linhaInicial,
                colunaInicial,
                numeroLinhas,
                numeroColunas,
                status: normalizeDoorStatus(primary.doorStatus),
                configuracoes,
            });

            return;
        }

        const configuracoes = porta.id_produto
            ? [
                {
                    id_produto: porta.id_produto,
                    quantidade: porta.qtd_produto ?? 0,
                }
            ]
            : [];

        result.push({
            numeroLogico: porta.numero_logico,
            portasFisicas: [physIdx + 1],
            linhaInicial: Math.floor(physIdx / cols),
            colunaInicial: physIdx % cols,
            numeroLinhas: 1,
            numeroColunas: 1,
            status: normalizeDoorStatus(porta.status),
            configuracoes,
        });
    });

    return result;
}